/**
 * Cash Casino — the SERVER spin session (Phase D, plan §7 Phase D, contract C0.1).
 *
 * This is the same seam as `LocalSpinSession`, wired to the real machine: the reels come from
 * `POST /api/casino/spin`, and every rupee on screen is one the server told us about. `Page.tsx`
 * swaps ONE import line; everything else it does with a session — spin, animate, settle — is
 * unchanged.
 *
 * ── THE ONE SUBTLETY THIS WHOLE FILE EXISTS FOR ───────────────────────────────────────────
 * `/spin` DEBITS AND DECIDES IN ONE ATOMIC TRANSACTION (`shared/src/games/casino/model.js`:
 * the row is born `settled`). By the time the ack reaches this file the money has ALREADY
 * moved both ways — the stake out and the payout in — and `ack.wallet` is the balance AFTER
 * both. But the player has not seen a single reel stop yet. So a naive client would jump the
 * balance up before the picture landed, which is the outcome told a second and a half early.
 *
 * So the session WITHHOLDS the payout: from the ack until `settle()` (the Page's reveal moment,
 * fired when the third reel stops) the displayed balance is `ack.wallet − payout`, i.e. exactly
 * "your stake has gone". `settle()` releases the hold, the balance rises by the payout, and the
 * Page credits nothing itself — the animation is theatre over a settled fact, and this file is
 * the curtain. Nothing here decides anything: there is no local RNG, no `naturalSpin`, and the
 * reels can only ever land where the server put them.
 *
 * ── WHY `spin()` IS AWAITED AND `LocalSpinSession`'s IS NOT ───────────────────────────────
 * The Page reads `result.stops[i]` SYNCHRONOUSLY to aim each reel (`Page.tsx` doSpin), so the
 * decision must be in hand before the first reel moves — and on a real table the decision is a
 * network round trip away. `SpinSession.spin` therefore returns `SpinOutcome | Promise<SpinOutcome>`
 * and the Page awaits it: the practice class stays byte-for-byte synchronous, and this one gets
 * its ~100 ms. The happy side effect is that a REFUSAL (not enough balance, the daily loss cap,
 * a locked game) arrives BEFORE the reels start, so the machine never spins on a spin that did
 * not happen. (2026-09-06.)
 *
 * ── WHY THERE IS NO CLOCK SYNC HERE ───────────────────────────────────────────────────────
 * The shared-table games run `createClockSync` because a betting window closes at a server
 * `locks_at` and a device clock minutes fast would grey the button early. Cash Casino is solo:
 * there is no window, no countdown, and nothing on screen is anchored to an absolute server
 * time. The only time-dependent server gate is `min_spin_interval_ms`, and that is an ELAPSED
 * measure, not an instant — `performance.now()` bounds it conservatively (see
 * `waitOutCooldown`), so a `/time` burst every 30 s would buy nothing and spend a real budget.
 */

import { api, ApiError } from '@baaligames/shared-ui/lib/api';
import { setWalletBalance, refreshWallet } from '@baaligames/shared-ui/lib/wallet';
import { createPoller, type Poller } from '../_shared/poller';
import {
  type Grid, type Evaluation, type LineWin, type Symbol as Sym,
  SYMBOLS, PAYLINES, STOPS, gridFor, evaluate,
} from './engine';
import { LocalSpinSession, type SpinSession, type SpinOutcome, type SpinResult, type Wallet } from './session';

// Re-exported so `Page.tsx`'s single import line can name everything it used to name from
// `./session` plus this implementation.
export { LocalSpinSession } from './session';
export type { SpinSession, SpinOutcome, SpinResult, Wallet } from './session';

/* ══════════════════════════════════════════════════════════════════════════════════════════
 * Wire types — exactly what `shared/src/games/casino/model.js` serialises (its `spinView` /
 * `publicSpinRow` whitelists and `getStateFor`, not a DB row)
 * ════════════════════════════════════════════════════════════════════════════════════════ */

type SrvLine = { line: number; symbol: string; x: number };

/** `model.js` `spinView()` / `replayView()`. `stops`/`grid` are null only on a replay whose
 *  stored result JSON was unreadable — handled, never assumed away. */
type SpinAck = {
  spinId: number;
  stake: number;
  stops: number[] | null;
  grid: Grid | null;
  lines: SrvLine[];
  scatter: boolean;
  totalX: number;
  payout: number;
  net: number;
  wallet: number;
  duplicate?: boolean;
  settledAt?: string | null;
};

/** `model.js` `publicSpinRow()` — the history serialiser. */
type SrvSpinRow = {
  spinId: number; stake: number; payout: number; net: number;
  stops: number[] | null; totalX: number; settledAt: string | null;
};

/** `model.js` `getStateFor()`. */
type CasinoState = {
  enabled: boolean;
  maintenanceMessage: string;
  serverNow: number;
  wallet: number;
  bet: { min: number; max: number; presets: number[] };
  spinIntervalMs: number;
  recent: SrvSpinRow[];
};

/* ══════════════════════════════════════════════════════════════════════════════════════════
 * Constants
 * ════════════════════════════════════════════════════════════════════════════════════════ */

/** Nothing on this screen changes without the player touching it, so a shared-table cadence
 *  would be pure waste. This is a freshness sweep — someone's deposit clears, or the same
 *  account plays in another tab — and it is what recovers the balance after a failed write.
 *  Well inside the 300/min the per-user limiter allows `/state`. */
const IDLE_POLL_MS = 10_000;
/** While a `/spin` is in flight: the ack itself kicks the poller, this only covers the gap. */
const FAST_POLL_MS = 1_200;
/** Added to the pacing wait so a round trip cannot land a millisecond early on the DB clock. */
const PACING_MARGIN_MS = 150;
/** Backoff between attempts at the SAME idempotency key after a network drop or a 5xx. */
const RETRY_MS = [400, 900];

const sleep = (ms: number) => new Promise<void>((res) => { window.setTimeout(res, ms); });

/* ══════════════════════════════════════════════════════════════════════════════════════════
 * Pure helpers — nothing unvalidated reaches the renderer
 * ════════════════════════════════════════════════════════════════════════════════════════ */

/** A wire stop triple → the tuple the Page aims the reels with, or null. A stop outside
 *  0..31 would translate the strip to a position that holds no symbol, so it is refused
 *  rather than drawn. */
export function asStops(v: unknown): [number, number, number] | null {
  if (!Array.isArray(v) || v.length !== 3) return null;
  const s = v.map((n) => Number(n));
  if (!s.every((n) => Number.isInteger(n) && n >= 0 && n < STOPS)) return null;
  return [s[0], s[1], s[2]];
}

const SYMBOL_SET = new Set<string>(SYMBOLS as readonly string[]);

/** The server's winning lines → `LineWin[]`. The Page indexes `PAYLINES[l.line]` to light the
 *  cells, so a line number it could not index would throw inside a render; a malformed entry
 *  is dropped instead. The MONEY is `ack.payout` either way — this list only lights lamps. */
export function toLines(raw: SrvLine[] | undefined | null): LineWin[] {
  if (!Array.isArray(raw)) return [];
  const out: LineWin[] = [];
  for (const l of raw) {
    const line = Number(l?.line);
    const x = Number(l?.x);
    const symbol = String(l?.symbol);
    if (!Number.isInteger(line) || line < 0 || line >= PAYLINES.length) continue;
    if (!Number.isFinite(x)) continue;
    if (symbol !== 'two-sevens' && !SYMBOL_SET.has(symbol)) continue;
    out.push({ line, symbol: symbol as Sym | 'two-sevens', x });
  }
  return out;
}

/** `?practice=1` → the Phase-A local session (dev sandbox), never the money path. */
function practiceRequested(): boolean {
  try { return new URLSearchParams(window.location.search).get('practice') === '1'; }
  catch { return false; }
}

const uuid = (): string => {
  try {
    const c = globalThis.crypto as Crypto | undefined;
    if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  } catch { /* older webview */ }
  return `cs-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

/* ══════════════════════════════════════════════════════════════════════════════════════════
 * The session
 * ════════════════════════════════════════════════════════════════════════════════════════ */

export class ServerSpinSession implements SpinSession {
  private poller: Poller;
  private subs = new Set<() => void>();
  private dead = false;

  /** The last `/state` we believe. Null until the first poll lands. */
  private state: CasinoState | null = null;

  /** The server's balance, and the payout being withheld until the reels stop. */
  private serverWallet = 0;
  private walletKnown = false;
  private held = 0;
  /** Bumped on every acked spin, so a `/state` already in flight cannot rewind it. */
  private writeSeq = 0;

  /** Decided by the server, not yet revealed to the player. */
  private open: SpinResult | null = null;
  private inFlight = false;
  /** Revealed spins, newest first — the ones it is safe to show. */
  private revealed: SpinResult[] = [];
  /** `performance.now()` of the last acked spin; the client half of the pacing gate. */
  private lastSpinAt: number | null = null;
  private lastErr: string | null = null;
  private paritySeen = false;

  /**
   * @param seed   the Phase-A local seed. Meaningless on the real machine — the reels are the
   *               server's — but kept in the signature so `Page.tsx` needs no change beyond
   *               its import.
   * @param wallet the Page's balance sink. Driven to the server's number, never trusted.
   */
  constructor(private seed: number, private wallet: Wallet) {
    // Practice mode keeps the Phase-A session alive for the dev sandbox (`?practice=1`): a
    // constructor may hand back another object, so the swap costs the Page nothing.
    if (practiceRequested()) {
      return new LocalSpinSession(seed, wallet) as unknown as ServerSpinSession;
    }

    this.poller = createPoller({
      poll: () => this.pollState(),
      now: () => performance.now(),
      idleMs: IDLE_POLL_MS,
      fastMs: FAST_POLL_MS,
      busy: () => this.inFlight,
      // A 429 is the limiter telling us to be quiet, not a message for the player; the poller
      // has already backed off by the time this runs.
      onError: (e) => { if (e instanceof ApiError && e.status !== 429) this.lastErr = e.message; },
    });
    this.poller.start();
  }

  /* ── the SpinSession interface ───────────────────────────────────────────────────────── */

  /** Has the server told us the balance at least once? Until it has, the Page shows `₹—` and
   *  holds the machine closed — see the `PRACTICE_START` note in `Page.tsx` (2026-09-06). */
  walletReady(): boolean { return this.walletKnown; }

  /**
   * Play one spin. Awaited by the Page (see the header): the answer is the SERVER's, and the
   * reels do not move until it is in hand.
   *
   * A refusal is either `balance` (the player cannot afford it — the message the Page prints
   * itself is right) or `busy` (anything else; `lastError()` carries the server's own plain
   * sentence, which the Page prints instead of a guess).
   */
  async spin(stake: number): Promise<SpinOutcome> {
    if (this.dead) return { ok: false as const, reason: 'busy' as const };
    // One machine, one spin: the Page's own phase guard is the first fence, this is the one
    // that holds when two code paths (a tap and an AUTO tick) race.
    if (this.open || this.inFlight) return { ok: false as const, reason: 'busy' as const };

    const amount = Math.floor(Number(stake));
    if (!(amount > 0)) return { ok: false as const, reason: 'balance' as const };
    if (!this.walletKnown) {
      this.lastErr = 'Loading your balance…';
      return { ok: false as const, reason: 'busy' as const };
    }
    if (amount > this.displayBalance()) return { ok: false as const, reason: 'balance' as const };
    if (this.state && this.state.enabled === false) {
      this.lastErr = this.state.maintenanceMessage || 'Cash Casino is unavailable right now.';
      return { ok: false as const, reason: 'busy' as const };
    }

    this.inFlight = true;
    this.lastErr = null;
    try {
      await this.waitOutCooldown();
      if (this.dead) return { ok: false as const, reason: 'busy' as const };

      const ack = await this.postSpin(amount);
      if (this.dead) return { ok: false as const, reason: 'busy' as const };

      // The money has moved by now whatever happens next, so the balance is taken from the ack
      // FIRST and only then do we ask whether we can draw the picture.
      this.writeSeq += 1;
      this.serverWallet = Number(ack.wallet) || 0;
      this.walletKnown = true;
      this.lastSpinAt = performance.now();

      const stops = asStops(ack.stops);
      if (!stops) {
        // Unreachable on a healthy server (`decide.js` always returns three valid stops), but
        // if it ever happened the honest answer is to show no reels and the true balance —
        // never to invent a picture for a spin that really was played.
        this.held = 0;
        this.lastErr = 'That spin could not be drawn. Your balance is up to date.';
        this.pushWallet();
        this.poller.kick();
        return { ok: false as const, reason: 'busy' as const };
      }

      // The PICTURE is derived from the stops on the client's own strips, because that is
      // literally what the player will see the reels land on. The MONEY and the lit paylines
      // are the server's. `shared/src/games/casino/engine.js` is a line-for-line port of
      // `./engine.ts` and `casino_server_parity_test` asserts numeric identity over all 32,768
      // triples, so the two can only disagree if that gate has been broken — in which case say
      // so once, loudly, and still pay what the server paid.
      const grid = gridFor(stops);
      const ev: Evaluation = {
        lines: toLines(ack.lines),
        scatter: !!ack.scatter,
        totalX: Number(ack.totalX) || 0,
      };
      if (!this.paritySeen && evaluate(grid).totalX !== ev.totalX) {
        this.paritySeen = true;
        console.warn('[casino] reel strips disagree with the server — the payout shown is the server\'s');
      }

      const result: SpinResult = {
        id: String(ack.spinId),
        stake: Number(ack.stake) || amount,
        stops,
        grid,
        ev,
        paid: Math.max(0, Math.floor(Number(ack.payout) || 0)),
      };

      // THE CURTAIN: the payout is withheld from the displayed balance until `settle()`.
      this.open = result;
      this.held = result.paid;
      this.pushWallet();                    // the dip: −stake, and only −stake
      this.poller.kick();
      return { ok: true as const, result };
    } catch (e) {
      // Refused, or unconfirmable. Either way nothing is drawn and the balance is re-read.
      const err = e instanceof ApiError ? e : null;
      this.lastErr = err ? err.message : 'That spin did not go through.';
      this.poller.kick();
      // `insufficient` and `bad_amount` are the player's own affordability problem, so they get
      // the Page's plain money message; everything else prints the server's sentence.
      const reason = err && (err.code === 'insufficient' || err.code === 'bad_amount')
        ? 'balance' as const : 'busy' as const;
      return { ok: false as const, reason };
    } finally {
      this.inFlight = false;
      this.emit();
    }
  }

  /**
   * The reels have stopped: reveal what the server already paid. This adds no money — the
   * credit committed inside the spin transaction — it releases the hold that was hiding it.
   */
  settle(id: string): number {
    const r = this.open;
    if (!r || r.id !== id) return 0;
    this.open = null;
    this.held = 0;
    this.revealed = [r, ...this.revealed].slice(0, 20);
    this.pushWallet();
    this.emit();
    return r.paid;
  }

  /**
   * Spins it is safe to show, newest first: the ones revealed this session, then the server's
   * own recent rows for anything from before it. A spin that has been decided but not yet
   * revealed is filtered out by id — `/state.recent` carries it the instant it commits, and
   * showing it would spoil the reels that are still turning.
   */
  history(): SpinResult[] {
    const out: SpinResult[] = [...this.revealed];
    const seen = new Set(out.map((r) => r.id));
    for (const row of this.state?.recent ?? []) {
      const id = String(row.spinId);
      if (seen.has(id) || (this.open && this.open.id === id)) continue;
      const stops = asStops(row.stops);
      if (!stops) continue;
      const grid = gridFor(stops);
      // A history row carries only `stops` and `totalX`, so the lines are re-derived for the
      // dot's colour. The rupees shown are always the row's own `payout`.
      out.push({ id, stake: Number(row.stake), stops, grid, ev: evaluate(grid), paid: Number(row.payout) });
      seen.add(id);
    }
    return out.slice(0, 20);
  }

  destroy() {
    this.dead = true;
    try { this.poller?.stop(); } catch { /* already stopped */ }
    this.open = null;
    this.held = 0;
    this.subs.clear();
    // The game owned the balance while it was on screen; hand the rest of the app the truth.
    void refreshWallet();
  }

  /* ── extras (optional on the interface; safe for a later UI to read) ─────────────────── */

  /** Fires whenever something the Page might render changed and the `Wallet` seam alone would
   *  not have told it — the first `/state` landing on a player whose balance happens to be
   *  exactly the seeded 0, above all, which is the one case where `walletReady()` flips with
   *  no balance movement to trigger a re-render. */
  subscribe(cb: () => void) { this.subs.add(cb); return () => { this.subs.delete(cb); }; }

  /** The last thing the server said when it said no. Cleared at the start of every spin. */
  lastError(): string | null { return this.lastErr; }

  /** The server's stake bounds and chip presets — what the Page's hardcoded chips should
   *  eventually come from. */
  limits(): CasinoState['bet'] | null { return this.state?.bet ?? null; }

  /** The server-enforced gap between spins (ms), 0 when the admin has turned pacing off. */
  spinIntervalMs(): number { return Math.max(0, Number(this.state?.spinIntervalMs) || 0); }

  /** False while the admin has the game off or this player is on the disable list. */
  enabled(): boolean { return this.state ? !!this.state.enabled : true; }

  /* ── polling ─────────────────────────────────────────────────────────────────────────── */

  private async pollState(): Promise<void> {
    const seqAtStart = this.writeSeq;
    const st = await api.get<CasinoState>('/casino/state');
    if (this.dead) return;

    this.state = st;
    // A poll is only fresher than the last write if no write acked while it was in flight: the
    // ack carries the POST-spin balance, this read may predate it. When a spin did land, keep
    // what the ack told us and go round again immediately.
    if (seqAtStart === this.writeSeq) {
      this.serverWallet = Number(st.wallet) || 0;
      this.walletKnown = true;
      this.lastErr = null;
    } else {
      this.poller.kick();
    }
    this.pushWallet();
    this.emit();
  }

  /* ── writes ──────────────────────────────────────────────────────────────────────────── */

  /**
   * One spin, retried on the SAME idempotency key.
   *
   * This is the whole point of the key (`model.js`: the replay is looked up under the wallet
   * lock and hands back the STORED spin, byte-identical reels and all). A dropped connection
   * or a 503 after the transaction committed would otherwise leave the player debited with no
   * reels; re-asking with the same key either learns the answer or proves nothing happened. A
   * 429 is NOT retried — the limiter is asking us to stop — and neither is any other 4xx,
   * which is a refusal, not a lost answer.
   */
  private async postSpin(amount: number): Promise<SpinAck> {
    const idempotencyKey = uuid();
    let lastError: unknown = new ApiError('http_error', 'That spin did not go through.', 0);
    for (let attempt = 0; attempt <= RETRY_MS.length; attempt++) {
      try {
        return await api.post<SpinAck>('/casino/spin', { stake: amount, idempotencyKey });
      } catch (e) {
        lastError = e;
        const status = e instanceof ApiError ? e.status : 0;
        const retryable = status === 0 || status >= 500;
        if (!retryable || this.dead || attempt === RETRY_MS.length) break;
        await sleep(RETRY_MS[attempt]);
      }
    }
    throw lastError;
  }

  /**
   * Hold the tap until the server's own pacing gate (`min_spin_interval_ms`, enforced inside
   * the spin transaction on the DB clock) can possibly accept it.
   *
   * Both clocks measure an ELAPSED interval, never an instant, so no clock sync is needed and
   * none is possible to get wrong: our stopwatch starts when the previous ack ARRIVED (after
   * the server stamped `settled_at`) and stops when we send (before the server reads `NOW(3)`),
   * so the interval we measure is always SHORTER than the one the server will. Waiting until
   * ours clears is therefore conservative in the safe direction. It costs nothing in practice —
   * a full spin cycle already runs about 3 s against a 2 s gate — and it exists so a fast AUTO
   * run cannot be stopped dead by a `too_fast` 409 the player never caused.
   */
  private async waitOutCooldown(): Promise<void> {
    const gap = this.spinIntervalMs();
    if (!gap || this.lastSpinAt == null) return;
    const since = performance.now() - this.lastSpinAt;
    if (since >= gap) return;
    await sleep(Math.min(gap, Math.ceil(gap - since) + PACING_MARGIN_MS));
  }

  /* ── wallet ──────────────────────────────────────────────────────────────────────────── */

  /** The server's balance minus anything the reels have not shown yet. */
  private displayBalance(): number {
    const base = this.walletKnown ? this.serverWallet : this.wallet.balance();
    return Math.max(0, base - this.held);
  }

  /**
   * Drive the Page's balance sink to the number we believe, using only the `Wallet` seam — the
   * `debit` path is also what plays the dip animation, so the drop looks exactly as it did on
   * practice chips.
   *
   * `debit` can refuse (it will not go negative), but it cannot refuse here: it only refuses
   * when the current figure is below the amount asked, and the amount asked is
   * `current − target` with `target ≥ 0`, which is never larger than `current`.
   */
  private pushWallet() {
    if (!this.walletKnown) return;
    const target = this.displayBalance();
    const cur = this.wallet.balance();
    const d = target - cur;
    if (d > 0) this.wallet.credit(d);
    else if (d < 0) this.wallet.debit(-d);
    // The app-wide chip shows the SERVER balance, not the withheld one: this Page is a
    // full-screen overlay, so nothing that reads it is visible while a spin is being revealed.
    setWalletBalance(this.serverWallet);
  }

  private emit() {
    this.subs.forEach((cb) => { try { cb(); } catch { /* a bad subscriber must not stop the machine */ } });
  }
}

export default ServerSpinSession;
